import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { SERVICES } from "@/data/content";

import { API } from "@/lib/api";

const inputCls =
  "w-full rounded-2xl bg-[#F8F6F0] border border-[#0E1B31]/10 px-4 py-3.5 text-sm text-[#0E1B31] placeholder:text-[#6B7A8F]/60 focus:outline-none focus:border-[#C26D52] focus:bg-white focus:shadow-[0_0_0_4px_rgba(194,109,82,0.1)] transition-all duration-300";

export const ContactForm = ({ initialInterest = "", prefill = null }) => {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    interest: initialInterest,
    message: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (prefill) {
      setForm((f) => ({
        ...f,
        interest: prefill.interest || f.interest,
        message: prefill.message || f.message,
      }));
    }
  }, [prefill]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Συμπληρώστε τα υποχρεωτικά πεδία: ονοματεπώνυμο, email και μήνυμα.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Το μήνυμά σας εστάλη με επιτυχία. Θα επικοινωνήσουμε σύντομα μαζί σας.");
      setForm({ name: "", company: "", email: "", phone: "", interest: "", message: "" });
    } catch (err) {
      toast.error("Κάτι πήγε στραβά κατά την αποστολή. Ελέγξτε το email σας και δοκιμάστε ξανά.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form data-testid="contact-form" onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      <div>
        <label htmlFor="cf-name" className="block text-xs font-medium tracking-wide text-[#3B4A5E] mb-2">
          Ονοματεπώνυμο *
        </label>
        <input
          id="cf-name"
          data-testid="contact-input-name"
          className={inputCls}
          value={form.name}
          onChange={set("name")}
          placeholder="π.χ. Μαρία Παπαδοπούλου"
          required
        />
      </div>
      <div>
        <label htmlFor="cf-company" className="block text-xs font-medium tracking-wide text-[#3B4A5E] mb-2">
          Εταιρεία
        </label>
        <input
          id="cf-company"
          data-testid="contact-input-company"
          className={inputCls}
          value={form.company}
          onChange={set("company")}
          placeholder="Η εταιρεία σας"
        />
      </div>
      <div>
        <label htmlFor="cf-email" className="block text-xs font-medium tracking-wide text-[#3B4A5E] mb-2">
          Email *
        </label>
        <input
          id="cf-email"
          type="email"
          data-testid="contact-input-email"
          className={inputCls}
          value={form.email}
          onChange={set("email")}
          placeholder="you@company.gr"
          required
        />
      </div>
      <div>
        <label htmlFor="cf-phone" className="block text-xs font-medium tracking-wide text-[#3B4A5E] mb-2">
          Τηλέφωνο
        </label>
        <input
          id="cf-phone"
          data-testid="contact-input-phone"
          className={inputCls}
          value={form.phone}
          onChange={set("phone")}
          placeholder="+30 ..."
        />
      </div>
      <div className="sm:col-span-2">
        <label htmlFor="cf-interest" className="block text-xs font-medium tracking-wide text-[#3B4A5E] mb-2">
          Τι σας ενδιαφέρει
        </label>
        <select
          id="cf-interest"
          data-testid="contact-select-interest"
          className={inputCls}
          value={form.interest}
          onChange={set("interest")}
        >
          <option value="">Γενική συζήτηση για την επιχείρησή μου</option>
          {SERVICES.map((s) => (
            <option key={s.id} value={s.title}>
              {s.title}
            </option>
          ))}
        </select>
      </div>
      <div className="sm:col-span-2">
        <label htmlFor="cf-message" className="block text-xs font-medium tracking-wide text-[#3B4A5E] mb-2">
          Μήνυμα *
        </label>
        <textarea
          id="cf-message"
          rows={6}
          data-testid="contact-input-message"
          className={`${inputCls} resize-y`}
          value={form.message}
          onChange={set("message")}
          placeholder="Πείτε μας λίγα λόγια για την επιχείρησή σας και τι θέλετε να βελτιώσετε ή να πετύχετε."
          required
        />
      </div>
      <div className="sm:col-span-2">
        <button
          type="submit"
          data-testid="contact-submit-button"
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-full px-8 py-4 bg-[#0E1B31] text-[#F8F6F0] text-sm font-semibold tracking-wide hover:bg-[#C26D52] disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-300 shadow-[0_14px_30px_-14px_rgba(14,27,49,0.6)]"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {loading ? "Αποστολή..." : "Αποστολή Μηνύματος"}
        </button>
      </div>
    </form>
  );
};
